import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { type ancillaryBaggageModel } from '@/widgets/ancillary-baggage';

import { seatMapApi } from '../../seat-map/api';
import type { useProductsState } from './use-products-state';

type ProductsState = ReturnType<typeof useProductsState>;
type AncillariesStore = ReturnType<
  typeof ancillaryBaggageModel.useAncillariesStore
>;

export function useProductsPageLoadingState(
  offerId: string,
  products: ProductsState,
  ancillariesStore: AncillariesStore,
  /**
   * Gate for the seat-map availability check. QuickBookingPage flips this to
   * true only after the fare-change call settles, so /airflow/seatmap is not
   * fired against a fare context that's about to be replaced.
   * Defaults to enabled-from-start to preserve the classic ProductsPage flow.
   */
  seatMapFetchEnabled?: Ref<boolean>,
) {
  const checkSeatMapQuery = useQuery({
    queryKey: ['seatMapCheck', offerId],
    queryFn: () => seatMapApi.checkSeatMap(offerId),
    staleTime: Infinity,
    enabled: seatMapFetchEnabled,
  });

  const isProductsLoading = computed(
    () =>
      products.isLoading.value ||
      checkSeatMapQuery.isLoading.value ||
      ancillariesStore.isAncillariesLoading,
  );

  const isEmptyPage = computed(
    () =>
      !(
        products.packages.value.length ||
        products.visibleProducts.value.length ||
        checkSeatMapQuery.data.value?.available ||
        ancillariesStore.isBaggageAvailable ||
        isProductsLoading.value
      ),
  );

  return {
    checkSeatMapQuery,
    isProductsLoading,
    isEmptyPage,
  };
}
